import { onUnmounted, watch } from 'vue'
import type { Ref } from 'vue'

const LOCK_CLASS = 'scroll-locked'

/**
 * Столько едет вниз панель шита (анимация `p-drawer` в теме PrimeVue).
 * Снять блокировку раньше нельзя: `window.scrollTo` вернёт страницу на место
 * прямо под уезжающим шитом, и это читается как рывок.
 */
const UNLOCK_DELAY = 500

/** Сколько оверлеев сейчас держат блокировку — вложенные шиты не снимают чужую. */
let lockCount = 0
/** Позиция страницы на момент блокировки — восстанавливается при снятии. */
let savedScrollY = 0

/**
 * Страница сейчас зафиксирована под оверлеем.
 *
 * Пока держится блокировка, `window.scrollY` равен нулю: страница висит на
 * `position: fixed` со сдвигом. Всем, кто следит за скроллом, этот ноль читать
 * нельзя — он означает не «вернулись наверх», а «страница отложена».
 */
export function isScrollLocked(): boolean {
  return lockCount > 0
}

function setLock(locked: boolean): void {
  const wasLocked = lockCount > 0
  lockCount = Math.max(0, lockCount + (locked ? 1 : -1))
  const isLocked = lockCount > 0
  if (isLocked === wasLocked) return

  const { body } = document
  if (isLocked) {
    /*
     * body фиксируется со сдвигом на текущий скролл: просто overflow: hidden
     * на html сбрасывает позицию страницы в 0 (проверено в Chrome).
     */
    savedScrollY = window.scrollY
    document.documentElement.classList.add(LOCK_CLASS)
    body.style.position = 'fixed'
    body.style.top = `-${savedScrollY}px`
    body.style.insetInline = '0'
  } else {
    document.documentElement.classList.remove(LOCK_CLASS)
    body.style.position = ''
    body.style.top = ''
    body.style.insetInline = ''
    window.scrollTo(0, savedScrollY)
  }
}

/**
 * Блокирует скролл страницы, пока `isActive` истинно — скроллится только
 * контент оверлея. PrimeVue-шный blockScroll завязан на afterEnter
 * транзишена и срабатывает нестабильно, поэтому блокируем сами.
 */
export function useScrollLock(isActive: Ref<boolean>): void {
  /* Своя блокировка этого оверлея — общий счётчик трогаем строго по разу */
  let isHeld = false
  let release: ReturnType<typeof setTimeout> | undefined

  function hold(): void {
    if (isHeld) return
    isHeld = true
    setLock(true)
  }

  function free(): void {
    if (!isHeld) return
    isHeld = false
    setLock(false)
  }

  watch(isActive, (value, prev) => {
    if (value === prev) return

    clearTimeout(release)
    if (value) return hold()

    release = setTimeout(free, UNLOCK_DELAY)
  })

  onUnmounted(() => {
    clearTimeout(release)
    free()
  })
}
